/**
 * Klaviyo tracking for the contact form section ([data-klaviyo-contact]):
 * identifies the visitor, records the section's event ("Contact Form" by
 * default) and, when the marketing consent box is ticked, subscribes the
 * email to the section's list.
 * The theme's own submit runs once the calls are sent (at most 1.5s).
 */
(() => {
  const API_REVISION = '2025-01-15';
  const MAX_WAIT = 1500;

  const getCompanyId = () => {
    const script = document.querySelector('script[src*="klaviyo.js"]');
    if (!script) return '';
    const match = script.src.match(/[?&]company_id=([A-Za-z0-9]+)/);
    return match ? match[1] : '';
  };

  const toPhoneE164 = (phone) => {
    const digits = phone.replace(/[^\d+]/g, '').replace(/^\+?61/, '0');
    return /^0[2-478]\d{8}$/.test(digits) ? '+61' + digits.slice(1) : '';
  };

  const getFormData = (form) => {
    const value = (selector) => {
      const field = form.querySelector(selector);
      return field ? field.value.trim() : '';
    };
    const textField = (pattern) => {
      const field = Array.from(form.querySelectorAll('input[type="text"]')).find((input) =>
        pattern.test(input.name)
      );
      return field ? field.value.trim() : '';
    };
    const consent = form.querySelector('input[name="contact[Marketing consent]"]');
    const firstName = textField(/^contact\[first ?name\]$/i);
    const lastName = textField(/^contact\[last ?name\]$/i);
    return {
      name: textField(/^contact\[(full ?)?name\]$/i) || [firstName, lastName].filter(Boolean).join(' '),
      company: textField(/^contact\[company( name)?\]$/i),
      email: value('input[type="email"]'),
      phone: value('input[type="tel"]'),
      message: value('textarea[name="contact[body]"]'),
      consent: consent ? consent.checked : false,
    };
  };

  const clientApi = (companyId, endpoint, data) =>
    fetch(`https://a.klaviyo.com/client/${endpoint}?company_id=${companyId}`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/vnd.api+json', revision: API_REVISION },
      body: JSON.stringify({ data }),
    }).then((response) => {
      if (!response.ok) {
        response.text().then((body) => console.warn(`Klaviyo ${endpoint} ${response.status}:`, body));
      }
      return response;
    });

  const track = (form, listId, metric, formType, signupOffer) => {
    const companyId = getCompanyId();
    const formData = getFormData(form);
    if (!companyId || !formData.email) return Promise.resolve();

    const profile = { email: formData.email };
    if (formData.company) profile.organization = formData.company;
    if (formData.name) {
      const nameParts = formData.name.split(/\s+/);
      profile.first_name = nameParts[0];
      if (nameParts.length > 1) profile.last_name = nameParts.slice(1).join(' ');
    }
    const phoneE164 = formData.phone ? toPhoneE164(formData.phone) : '';
    if (phoneE164) profile.phone_number = phoneE164;

    const calls = [];

    if (typeof window.klaviyo !== 'undefined') {
      const identifyData = { $email: profile.email };
      if (profile.first_name) identifyData.$first_name = profile.first_name;
      if (profile.last_name) identifyData.$last_name = profile.last_name;
      if (profile.phone_number) identifyData.$phone_number = profile.phone_number;
      if (profile.organization) identifyData.$organization = profile.organization;
      calls.push(Promise.resolve(window.klaviyo.identify(identifyData)));
    }

    const sendEvent = (eventProfile) =>
      clientApi(companyId, 'events', {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: eventProfile } },
          properties: {
            form_type: formType,
            page_url: window.location.href,
            name: formData.name,
            company: formData.company,
            phone: formData.phone,
            message: formData.message,
            marketing_consent: formData.consent,
          },
          time: new Date().toISOString(),
        },
      });
    calls.push(
      sendEvent(profile).then((response) => {
        if (response.status !== 400 || !profile.phone_number) return response;
        const { phone_number, ...profileWithoutPhone } = profile;
        return sendEvent(profileWithoutPhone);
      })
    );

    if (formData.consent && listId) {
      if (signupOffer && window.klaviyoSignup) {
        calls.push(window.klaviyoSignup.track(formData.email, formType));
      }
      calls.push(
        clientApi(companyId, 'subscriptions', {
          type: 'subscription',
          attributes: {
            custom_source: metric,
            profile: { data: { type: 'profile', attributes: { email: formData.email } } },
          },
          relationships: { list: { data: { type: 'list', id: listId } } },
        })
      );
    }

    return Promise.all(calls);
  };

  const init = (wrapper) => {
    const form = wrapper.querySelector('form');
    if (!form) return;
    const listId = wrapper.dataset.klaviyoList || '';
    const metric = wrapper.dataset.klaviyoMetric || 'Contact Form';
    const formType = wrapper.dataset.formType || 'contact';
    const signupOffer = wrapper.hasAttribute('data-signup-offer');
    const metricSlug = metric
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^[_0-9]+|_+$/g, '')
      .slice(0, 30);
    const analyticsEvent =
      metric === 'Contact Form' || !metricSlug ? 'contact_enquiry_submitted' : `${metricSlug}_submitted`;
    let pending = false;
    let releasing = false;

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) pending = false;
    });

    form.addEventListener(
      'submit',
      (event) => {
        if (releasing || event.defaultPrevented) return;
        if (pending) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        if (!form.checkValidity()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        pending = true;
        if (window.themeAnalytics) {
          window.themeAnalytics.trackEvent(analyticsEvent, { form_type: formType });
        }
        const timeout = new Promise((resolve) => setTimeout(resolve, MAX_WAIT));
        Promise.race([Promise.resolve().then(() => track(form, listId, metric, formType, signupOffer)), timeout])
          .catch((error) => console.error('Klaviyo contact form tracking failed:', error))
          .finally(() =>
            setTimeout(() => {
              releasing = true;
              if (form.requestSubmit) form.requestSubmit();
              else form.submit();
              releasing = false;
              pending = form.classList.contains('processing') || !form.hasValidator;
            })
          );
      },
      true
    );
  };

  const initAll = () => document.querySelectorAll('[data-klaviyo-contact]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
